import React, { useState, useEffect } from 'react';
import { Box, Button, Chip, Avatar, Typography, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel, Grid, Checkbox, FormControlLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchWorkers, addWorker, updateWorker, deleteWorker } from '../../services/workerService';
import type { Worker, WorkerRole, PaymentType, WorkerStatus } from '../../types/worker';
import { defaultPermissions } from '../../types/worker';
import { v4 as uuidv4 } from 'uuid';

const workerRoles: WorkerRole[] = ['owner', 'manager', 'worker', 'viewer'];
const paymentTypes: PaymentType[] = ['daily', 'weekly', 'monthly'];
const workerStatuses: WorkerStatus[] = ['active', 'on-leave', 'inactive'];

// Add specialization options
const specializationOptions = [
  'Livestock', 'Crop', 'Machinery', 'Logistics', 'Veterinary', 'Irrigation', 'Security', 'Cleaning', 'Other'
];

interface WorkerListProps {
  farmId: string;
}

const WorkerList: React.FC<WorkerListProps> = ({ farmId }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newWorker, setNewWorker] = useState<Partial<Worker>>({
    name: '',
    role: 'worker',
    phoneNumber: '',
    email: '',
    startDate: '',
    wage: 0,
    paymentType: 'monthly',
    status: 'active',
    specialization: [],
    workingHours: { start: '08:00', end: '17:00' },
    leaveDays: 0,
    leaveHistory: [],
    emergencyContact: { name: '', relationship: '', phone: '' },
    notes: '',
    permissions: defaultPermissions['worker'],
  });

  // Add avatar upload state
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const [editWorker, setEditWorker] = useState<Worker | null>(null);
  const [viewWorker, setViewWorker] = useState<Worker | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean, worker: Worker | null }>({ open: false, worker: null });

  useEffect(() => {
    setLoading(true);
    fetchWorkers(farmId)
      .then(data => setWorkers(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [farmId]);

  const handleNewWorkerChange = (field: keyof Worker, value: any) => {
    setNewWorker(prev => ({ ...prev, [field]: value }));
  };

  // Handle avatar file change
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleAddWorkerSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const workerToAdd: any = {};
      Object.entries(newWorker).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          workerToAdd[key] = value;
        }
      });
      workerToAdd.farmId = farmId;
      const newWorkerData = await addWorker(workerToAdd);
      setWorkers(prev => [...prev, newWorkerData]);
      setSuccess('Worker added successfully!');
      setOpen(false);
      setNewWorker({
        name: '',
        role: 'worker',
        phoneNumber: '',
        email: '',
        startDate: '',
        wage: 0,
        paymentType: 'monthly',
        status: 'active',
        specialization: [],
        workingHours: { start: '08:00', end: '17:00' },
        leaveDays: 0,
        leaveHistory: [],
        emergencyContact: { name: '', relationship: '', phone: '' },
        notes: '',
        permissions: defaultPermissions['worker'],
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditWorkerSubmit = async () => {
    if (!editWorker) return;
    setLoading(true);
    setError(null);
    try {
      const updated = await updateWorker(editWorker.id, editWorker);
      setWorkers(prev => prev.map(w => w.id === updated.id ? updated : w));
      setEditWorker(null);
      setSuccess('Worker updated successfully!');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorker = async () => {
    if (!deleteConfirm.worker) return;
    setLoading(true);
    setError(null);
    try {
      await deleteWorker(deleteConfirm.worker.id);
      setWorkers(prev => prev.filter(w => w.id !== deleteConfirm.worker!.id));
      setDeleteConfirm({ open: false, worker: null });
      setSuccess('Worker deleted successfully!');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search workers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
          sx={{ width: 300 }}
        />
      </Box>
      {loading && <Typography>Loading workers...</Typography>}
      {error && <Typography color="error">{error}</Typography>}
      {success && <Typography color="success.main">{success}</Typography>}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Avatar</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workers.filter((w: Worker) => w.name.toLowerCase().includes(search.toLowerCase())).map((worker: Worker) => (
              <TableRow key={worker.id} hover>
                <TableCell><Avatar>{worker.name[0]}</Avatar></TableCell>
                <TableCell>{worker.name}</TableCell>
                <TableCell>{worker.role}</TableCell>
                <TableCell>
                  <Chip label={worker.status.charAt(0).toUpperCase() + worker.status.slice(1)} color={worker.status === 'active' ? 'success' : worker.status === 'on-leave' ? 'warning' : 'default'} size="small" />
                </TableCell>
                <TableCell>{worker.phoneNumber}</TableCell>
                <TableCell>{worker.email}</TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => setViewWorker(worker)}><VisibilityIcon /></IconButton>
                  <IconButton color="secondary" onClick={() => setEditWorker(worker)}><EditIcon /></IconButton>
                  <IconButton color="error" onClick={() => setDeleteConfirm({ open: true, worker })}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Worker</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Avatar Upload */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={avatarPreview || undefined} sx={{ width: 56, height: 56 }} />
                <Button variant="outlined" component="label">
                  Upload Avatar
                  <input type="file" accept="image/*" hidden onChange={handleAvatarChange} />
                </Button>
              </Box>
            </Grid>
            {/* Full Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={newWorker.name}
                onChange={e => handleNewWorkerChange('name', e.target.value)}
                required
              />
            </Grid>
            {/* Role */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newWorker.role}
                  label="Role"
                  onChange={e => handleNewWorkerChange('role', e.target.value)}
                >
                  {workerRoles.map(role => (
                    <MenuItem key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Specialization */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Specialization</InputLabel>
                <Select
                  multiple
                  value={newWorker.specialization || []}
                  label="Specialization"
                  onChange={e => handleNewWorkerChange('specialization', e.target.value)}
                  renderValue={(selected) => Array.isArray(selected) ? selected.join(', ') : ''}
                >
                  {specializationOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={newWorker.specialization?.includes(option) || false} />
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Phone Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newWorker.phoneNumber}
                onChange={e => handleNewWorkerChange('phoneNumber', e.target.value)}
                required
              />
            </Grid>
            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newWorker.email}
                onChange={e => handleNewWorkerChange('email', e.target.value)}
                required
                error={!!newWorker.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newWorker.email)}
                helperText={!!newWorker.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newWorker.email) ? 'Invalid email' : ''}
              />
            </Grid>
            {/* Start Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={newWorker.startDate}
                onChange={e => handleNewWorkerChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            {/* Wage */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Wage"
                value={newWorker.wage}
                onChange={e => handleNewWorkerChange('wage', Number(e.target.value))}
                required
                inputProps={{ min: 0 }}
                error={!!newWorker.wage && newWorker.wage <= 0}
                helperText={!!newWorker.wage && newWorker.wage <= 0 ? 'Wage must be greater than 0' : ''}
              />
            </Grid>
            {/* Payment Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Type</InputLabel>
                <Select
                  value={newWorker.paymentType}
                  label="Payment Type"
                  onChange={e => handleNewWorkerChange('paymentType', e.target.value)}
                >
                  {paymentTypes.map(type => (
                    <MenuItem key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newWorker.status}
                  label="Status"
                  onChange={e => handleNewWorkerChange('status', e.target.value)}
                >
                  {workerStatuses.map(status => (
                    <MenuItem key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Working Hours Start */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Working Hours Start"
                value={newWorker.workingHours?.start || ''}
                onChange={e => handleNewWorkerChange('workingHours', { ...newWorker.workingHours, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            {/* Working Hours End */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Working Hours End"
                value={newWorker.workingHours?.end || ''}
                onChange={e => handleNewWorkerChange('workingHours', { ...newWorker.workingHours, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            {/* Leave Days */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Leave Days"
                value={newWorker.leaveDays}
                onChange={e => handleNewWorkerChange('leaveDays', Number(e.target.value))}
                inputProps={{ min: 0 }}
              />
            </Grid>
            {/* Emergency Contact */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={newWorker.emergencyContact?.name || ''}
                onChange={e => handleNewWorkerChange('emergencyContact', { ...newWorker.emergencyContact, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Relationship"
                value={newWorker.emergencyContact?.relationship || ''}
                onChange={e => handleNewWorkerChange('emergencyContact', { ...newWorker.emergencyContact, relationship: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={newWorker.emergencyContact?.phone || ''}
                onChange={e => handleNewWorkerChange('emergencyContact', { ...newWorker.emergencyContact, phone: e.target.value })}
              />
            </Grid>
            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={newWorker.notes}
                onChange={e => handleNewWorkerChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddWorkerSubmit} 
            disabled={
              !newWorker.name ||
              !newWorker.role ||
              !newWorker.email ||
              !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newWorker.email || '') ||
              !newWorker.phoneNumber ||
              !newWorker.startDate ||
              !newWorker.wage || newWorker.wage <= 0
            }
          >Add</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Worker Dialog */}
      <Dialog open={!!editWorker} onClose={() => setEditWorker(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Worker</DialogTitle>
        <DialogContent>
          {editWorker && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* All fields as in add worker, but bound to editWorker */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={editWorker.name}
                  onChange={e => setEditWorker({ ...editWorker, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={editWorker.role}
                    label="Role"
                    onChange={e => setEditWorker({ ...editWorker, role: e.target.value as any })}
                  >
                    {workerRoles.map(role => (
                      <MenuItem key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Specialization</InputLabel>
                  <Select
                    multiple
                    value={editWorker.specialization || []}
                    label="Specialization"
                    onChange={e => setEditWorker({ ...editWorker, specialization: e.target.value as any })}
                    renderValue={(selected) => Array.isArray(selected) ? selected.join(', ') : ''}
                  >
                    {specializationOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={editWorker.specialization?.includes(option) || false} />
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={editWorker.phoneNumber}
                  onChange={e => setEditWorker({ ...editWorker, phoneNumber: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editWorker.email}
                  onChange={e => setEditWorker({ ...editWorker, email: e.target.value })}
                  required
                  error={!!editWorker.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editWorker.email)}
                  helperText={!!editWorker.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editWorker.email) ? 'Invalid email' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={editWorker.startDate}
                  onChange={e => setEditWorker({ ...editWorker, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Wage"
                  value={editWorker.wage}
                  onChange={e => setEditWorker({ ...editWorker, wage: Number(e.target.value) })}
                  required
                  inputProps={{ min: 0 }}
                  error={!!editWorker.wage && editWorker.wage <= 0}
                  helperText={!!editWorker.wage && editWorker.wage <= 0 ? 'Wage must be greater than 0' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Type</InputLabel>
                  <Select
                    value={editWorker.paymentType}
                    label="Payment Type"
                    onChange={e => setEditWorker({ ...editWorker, paymentType: e.target.value as any })}
                  >
                    {paymentTypes.map(type => (
                      <MenuItem key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editWorker.status}
                    label="Status"
                    onChange={e => setEditWorker({ ...editWorker, status: e.target.value as any })}
                  >
                    {workerStatuses.map(status => (
                      <MenuItem key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Working Hours Start"
                  value={editWorker.workingHours?.start || ''}
                  onChange={e => setEditWorker({ ...editWorker, workingHours: { ...editWorker.workingHours, start: e.target.value } })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Working Hours End"
                  value={editWorker.workingHours?.end || ''}
                  onChange={e => setEditWorker({ ...editWorker, workingHours: { ...editWorker.workingHours, end: e.target.value } })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Leave Days"
                  value={editWorker.leaveDays}
                  onChange={e => setEditWorker({ ...editWorker, leaveDays: Number(e.target.value) })}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Name"
                  value={editWorker.emergencyContact?.name || ''}
                  onChange={e => setEditWorker({ ...editWorker, emergencyContact: { ...editWorker.emergencyContact, name: e.target.value } })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Relationship"
                  value={editWorker.emergencyContact?.relationship || ''}
                  onChange={e => setEditWorker({ ...editWorker, emergencyContact: { ...editWorker.emergencyContact, relationship: e.target.value } })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Phone"
                  value={editWorker.emergencyContact?.phone || ''}
                  onChange={e => setEditWorker({ ...editWorker, emergencyContact: { ...editWorker.emergencyContact, phone: e.target.value } })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  value={editWorker.notes}
                  onChange={e => setEditWorker({ ...editWorker, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditWorker(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditWorkerSubmit} disabled={!editWorker || !editWorker.name || !editWorker.role || !editWorker.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editWorker.email || '') || !editWorker.phoneNumber || !editWorker.startDate || !editWorker.wage || editWorker.wage <= 0}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* View Worker Dialog */}
      <Dialog open={!!viewWorker} onClose={() => setViewWorker(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Worker Details</DialogTitle>
        <DialogContent>
          {viewWorker && (
            <Box>
              <Typography variant="h6">{viewWorker.name}</Typography>
              <Typography>Email: {viewWorker.email}</Typography>
              <Typography>Phone: {viewWorker.phoneNumber}</Typography>
              <Typography>Role: {viewWorker.role}</Typography>
              <Typography>Status: {viewWorker.status}</Typography>
              <Typography>Specialization: {viewWorker.specialization?.join(', ')}</Typography>
              <Typography>Wage: {viewWorker.wage}</Typography>
              <Typography>Payment Type: {viewWorker.paymentType}</Typography>
              <Typography>Working Hours: {viewWorker.workingHours?.start} - {viewWorker.workingHours?.end}</Typography>
              <Typography>Leave Days: {viewWorker.leaveDays}</Typography>
              <Typography>Emergency Contact: {viewWorker.emergencyContact?.name} ({viewWorker.emergencyContact?.relationship}) - {viewWorker.emergencyContact?.phone}</Typography>
              <Typography>Notes: {viewWorker.notes}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewWorker(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, worker: null })}>
        <DialogTitle>Delete Worker</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {deleteConfirm.worker?.name}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, worker: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteWorker}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkerList; 